import DatePicker from "@/components/date-picker";
import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import {
  AsyncAutocompleteSelect,
  Option,
} from "@/components/select/AsyncAutocompleteSelect";
import { SelectComponent } from "@/components/select/Select";
import { InputField } from "@/components/input-field/InputField";
import { TextArea } from "@/components/text-area/TextArea";
import { AutocompleteSelect } from "@/components/select/AutocompleteSelect";
import { InputCurrency } from "@/components/input-currency/InputCurrency";
import { AppRoutes } from "@/constants/routes";
import { AppColors } from "@/styles/colors";
import { Card, Field, Flex, Grid, GridItem, Text } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { CategoriaServicio } from "@/constants/enums";
import { loadOptionsHandler } from "@/pages/grain-requests/sell-orders/schema/handler-functions";
import { getUsuariosMobileByCuenta } from "@/services/users.service";
import { getProcedencias } from "@/services/aux-entities.service";
import {
  CatalogoServicioItem,
  getCatalogoServicio,
} from "@/services/service-requests.service";
import {
  getCuentasByBusqueda,
  getSucursales,
  Sucursal,
} from "@/services/accounts.service";
import { onlyNumbers } from "@/utils/reg-expressions";
import {
  handleTonChangeInInput,
  isEmpty,
  parseTonInputToNumber,
} from "@/utils/query-string";
import { useRoles } from "@/hooks/useRoles";
import { flightRequestCreationSchema } from "../schema/flight-request-creation.schema";
import { createFlightRequestHandler } from "../schema/handler-functions";

type SelectItem = { label: string; value: string };

export const FlightRequestCreationPage = () => {
  const navigate = useNavigate();
  const { fetchPermissions } = useRoles();

  const [isActive, setIsActive] = useState(false);
  const [loadingCreate, setLoadingCreate] = useState(false);

  const {
    register,
    control,
    formState: { errors },
    setValue,
  } = useForm({
    resolver: zodResolver(flightRequestCreationSchema),
    defaultValues: {
      sucursal: [],
      hectareas: "",
      valorUnitario: "",
      valorTotal: "",
      observaciones: "",
    },
  });

  const watchedValues = useWatch({ control });
  const watchedAccount = useWatch({ control, name: "account" });
  const watchedServicio = useWatch({ control, name: "tipoServicio" });
  const watchedHectareas = useWatch({ control, name: "hectareas" });

  const handleCancel = () => {
    navigate(AppRoutes.FLIGHT_REQUESTS);
  };

  const [catalogo, setCatalogo] = useState<CatalogoServicioItem[]>([]);

  const handleAdd = async () => {
    await createFlightRequestHandler({
      values: watchedValues,
      setLoadingCreate,
      navigate,
      fetchPermissions,
      catalogo,
    });
  };

  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const getAccountOptions = async (inputValue: string) => {
    const filtered = await getCuentasByBusqueda({ busqueda: inputValue });
    return (
      filtered?.map((p) => ({
        label: `${p.titular} - ${p.nroCuenta} - ${p.tipoCuenta}`,
        value: p.nroCuenta,
        tipoCuenta: p.tipoCuenta,
        idTipoCuenta: p.idTipoCuenta,
        zonaComercial: p.zonaComercial,
      })) ?? []
    );
  };

  const loadAccountOptions = (inputValue: string): Promise<Option[]> => {
    return loadOptionsHandler({
      getOptions: getAccountOptions as any,
      inputValue,
      setLoadingPersons: setLoadingAccounts,
      timeoutRef,
    });
  };

  const [cuentaUsuarios, setCuentaUsuarios] = useState<any[]>([]);
  const [loadingCuentaUsuarios, setLoadingCuentaUsuarios] = useState(false);

  const fetchUsuariosByCuenta = async (cuenta?: string) => {
    if (!cuenta) return;
    setLoadingCuentaUsuarios(true);
    const response = await getUsuariosMobileByCuenta({ cuenta });
    setCuentaUsuarios(response ?? []);
    setLoadingCuentaUsuarios(false);
  };

  const [procedencias, setProcedencias] = useState<any[]>([]);
  const [loadingProcedencias, setLoadingProcedencias] = useState(false);

  const [loadingSucursales, setLoadingSucursales] = useState(false);
  const [sucursales, setSucursales] = useState<Sucursal[] | null>([]);

  const fetchSucursales = async () => {
    setLoadingSucursales(true);
    const response = await getSucursales();
    setSucursales(response);
    setLoadingSucursales(false);
  };

  const fetchProcedencias = async (cuenta?: string) => {
    if (!cuenta) return;
    const nro = Number(cuenta);
    if (!Number.isFinite(nro)) return;

    setLoadingProcedencias(true);
    const response = await getProcedencias({ cuenta: nro });
    setProcedencias(response ?? []);
    setLoadingProcedencias(false);
  };

  const procedenciaItems: SelectItem[] = useMemo(() => {
    return (
      procedencias?.map((p: any) => ({
        label: p?.Nombre ?? p?.nombre ?? "-",
        value: String(p?.Id ?? p?.id ?? ""),
      })) ?? []
    );
  }, [procedencias]);

  const [loadingCatalogo, setLoadingCatalogo] = useState(false);

  const fetchCatalogo = async () => {
    setLoadingCatalogo(true);
    try {
      const response = await getCatalogoServicio({
        Categoria: CategoriaServicio.Vuelo,
      });
      setCatalogo(response ?? []);
    } finally {
      setLoadingCatalogo(false);
    }
  };

  const tipoServicioItems: SelectItem[] = useMemo(() => {
    return (
      catalogo?.map((s: any) => ({
        label: String(s?.nombre ?? "-"),
        value: String(s?.id ?? ""),
      })) ?? []
    );
  }, [catalogo]);

  const selectedServicio = useMemo(() => {
    const v = Array.isArray(watchedServicio) ? watchedServicio?.[0] : watchedServicio;
    const id = Number(v);
    if (!Number.isFinite(id)) return undefined;
    return (catalogo ?? []).find((s: any) => Number(s?.id) === id);
  }, [watchedServicio, catalogo]);

  const valorUnitario = Number(selectedServicio?.valorUnitario ?? 0);
  const hectareasValue = parseTonInputToNumber(watchedHectareas) ?? 0;

  const hasNoFilledValues = useMemo(() => {
    const { account, tipoServicio, procedencia, fechaDeseada, hectareas } =
      watchedValues || {};

    return (
      isEmpty(account) ||
      isEmpty(tipoServicio) ||
      isEmpty(procedencia) ||
      isEmpty(fechaDeseada) ||
      isEmpty(hectareas) ||
      hectareasValue <= 0
    );
  }, [watchedValues, hectareasValue]);

  const valorTotal = Number.isFinite(valorUnitario)
    ? Number((hectareasValue * valorUnitario).toFixed(2))
    : 0;

  useEffect(() => {
    setValue("valorUnitario", valorUnitario ? String(valorUnitario) : "");
  }, [valorUnitario, setValue]);

  useEffect(() => {
    setValue("valorTotal", valorTotal ? String(valorTotal) : "");
  }, [valorTotal, setValue]);

  useEffect(() => {
    setIsActive(true);
  }, []);

  useEffect(() => {
    if (isActive) void fetchCatalogo();
  }, [isActive]);

  useEffect(() => {
    if (isActive) void fetchSucursales();
  }, [isActive]);

  useEffect(() => {
    if (!watchedAccount?.value) return;
    setValue("usuarioMobile", []);
    setValue("sucursal", []);
    setValue("procedencia", []);
    setProcedencias([]);
    void fetchUsuariosByCuenta(String(watchedAccount.value));
    void fetchProcedencias(String(watchedAccount.value));

    const claveSucursal = String(watchedAccount?.zonaComercial ?? "");
    if (claveSucursal) {
      setValue("sucursal", [claveSucursal]);
    }
  }, [watchedAccount, setValue]);

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        title="Agregar solicitud de vuelo"
        addButtonLabel="Agregar"
        cancelButtonLabel="Cancelar"
        addButtonType="button"
        handleAdd={handleAdd}
        handleCancel={handleCancel}
        disableAdd={hasNoFilledValues || loadingCreate}
        loading={loadingCreate}
      />

      <Card.Root
        maxW="full"
        marginTop="30px"
        borderRadius="8px"
      >
        <Card.Body
          paddingX="48px"
          paddingY="30px"
          borderRadius="8px"
          border={`1px solid ${AppColors.WHITE_BORDER_1}`}
        >
          <Grid templateColumns="repeat(4, 1fr)" gap={6} alignItems="end">
            <GridItem colSpan={1} paddingY="8px">
              <Field.Root invalid={!!errors?.account?.message} gap="12px">
                <Field.Label>
                  <Text fontSize="16px" fontWeight={600}>Cuenta</Text>
                </Field.Label>
                <AsyncAutocompleteSelect
                  name="account"
                  control={control}
                  loadOptions={loadAccountOptions}
                  placeholder="Seleccionar"
                  isLoading={loadingAccounts}
                  disabled={loadingCreate}
                  triggerWithLength={false}
                />
              </Field.Root>
            </GridItem>

            <GridItem colSpan={1} paddingY="8px">
              <SelectComponent
                fullWidth
                name="sucursal"
                control={control}
                labelText="Sucursal"
                placeholder="-"
                items={
                  sucursales?.map((s) => ({
                    label: s?.nombre,
                    value: s?.clave,
                  })) ?? []
                }
                loading={loadingSucursales}
                disabled
                closeOnSelect
                clearable={false}
                size="xs"
                minHeight="40px"
              />
            </GridItem>

            <GridItem colSpan={1} paddingY="8px">
              <Field.Root invalid={!!errors?.usuarioMobile?.message} gap="12px">
                <Field.Label>
                  <Text fontSize="16px" fontWeight={600}>Usuario Mobile</Text>
                </Field.Label>
                <AutocompleteSelect
                  control={control}
                  inputName="usuarioMobile"
                  loading={loadingCuentaUsuarios}
                  disabled={!watchedAccount || loadingCuentaUsuarios || loadingCreate}
                  multiple={false}
                  items={
                    cuentaUsuarios?.map((cu: any) => ({
                      label: `${cu?.nombre} ${cu?.apellido}`,
                      value: cu?.id,
                    })) ?? []
                  }
                />
              </Field.Root>
            </GridItem>

            <GridItem colSpan={1} paddingY="8px">
              <SelectComponent
                fullWidth
                name="tipoServicio"
                control={control}
                labelText="Tipo de vuelo"
                placeholder="Seleccionar"
                items={tipoServicioItems}
                loading={loadingCatalogo}
                disabled={loadingCatalogo || loadingCreate}
                closeOnSelect
                clearable
                size="xs"
                minHeight="40px"
              />
            </GridItem>

            <GridItem colSpan={1} paddingY="8px">
              <SelectComponent
                fullWidth
                name="procedencia"
                control={control}
                labelText="Establecimiento"
                placeholder="Seleccionar"
                items={procedenciaItems}
                loading={loadingProcedencias}
                disabled={!watchedAccount || loadingProcedencias || loadingCreate}
                closeOnSelect
                clearable
                size="xs"
                minHeight="40px"
              />
            </GridItem>

            <GridItem colSpan={1} paddingY="8px">
              <DatePicker
                control={control}
                name="fechaDeseada"
                labelText="Fecha deseada"
                placeholder="Seleccionar"
                minDate={dayjs()}
                disablePortal={false}
                minHeight="40px"
                disabled={loadingCreate}
              />
            </GridItem>

            <GridItem colSpan={1} paddingY="8px">
              <InputField
                inputName="hectareas"
                labelText="Hectáreas"
                placeholder="Ingresar"
                control={control}
                type="text"
                allowedPattern={onlyNumbers}
                onChange={handleTonChangeInInput}
                minHeight="40px"
                disabled={loadingCreate}
              />
            </GridItem>

            <GridItem colSpan={1} paddingY="8px">
              <Field.Root gap="12px">
                <Field.Label>
                  <Text fontSize="16px" fontWeight={600}>Valor Unitario</Text>
                </Field.Label>
                <InputCurrency
                  control={control}
                  name="valorUnitario"
                  height="40px"
                  maxLength={20}
                  disabled
                />
              </Field.Root>
            </GridItem>

            <GridItem colSpan={1} paddingY="8px">
              <Field.Root gap="12px">
                <Field.Label>
                  <Text fontSize="16px" fontWeight={600}>Valor Total del servicio</Text>
                </Field.Label>
                <InputCurrency
                  control={control}
                  name="valorTotal"
                  height="40px"
                  maxLength={20}
                  disabled
                />
              </Field.Root>
            </GridItem>
          </Grid>
        </Card.Body>
      </Card.Root>

      <Flex flexDir="column" gap="12px">
        <Text fontWeight={700} fontSize="20px">
          Detalle
        </Text>
        <TextArea
          register={register as any}
          errors={errors}
          inputName="observaciones"
          placeholder=""
          maxLength={50}
          helperText="Máximo 50 caracteres"
          disabled={loadingCreate}
        />
      </Flex>
    </Flex>
  );
};